/**
 * Derives the page context (route + entity ids) the assistant panel sends
 * with every request. Kept outside the component file so Fast Refresh keeps
 * working.
 */
import { matchPath } from "react-router-dom";

export type AgentPageSearchParam = { key: string; value: string };

export type PageContext = {
  path: string;
  projectId?: string;
  quoteId?: string;
  requirementId?: string;
  templateId?: string;
  crmCompanyId?: string;
  crmPersonId?: string;
  searchParams?: AgentPageSearchParam[];
};

/**
 * URL search-param keys the panel is allowed to forward to the assistant —
 * mirrors the backend's own allow-list (`AGENT_PAGE_SEARCH_PARAM_KEYS`,
 * `agentChatTurn.ts`), which is what actually enforces this: this frontend
 * copy only avoids sending a param the server would drop anyway.
 */
const AGENT_PAGE_SEARCH_PARAM_KEYS: readonly string[] = ["tab"];

/** Mirrors the backend's caps (`agentChatTurn.ts`) so a value trimmed here matches what the server would keep. */
const AGENT_PAGE_SEARCH_PARAM_VALUE_MAX_LENGTH = 100;
const AGENT_PAGE_SEARCH_PARAM_MAX_COUNT = 5;

/**
 * Filters the current page's URL search params down to the allow-listed
 * keys, capping value length and count. Returns `undefined` — never an
 * empty array — when nothing survives, so a page with no (or no
 * allow-listed) search params omits the field entirely.
 */
function deriveAgentPageSearchParams(
  search: string
): AgentPageSearchParam[] | undefined {
  if (!search) return undefined;
  const params = new URLSearchParams(search);
  const kept: AgentPageSearchParam[] = [];
  for (const [key, value] of params) {
    if (!AGENT_PAGE_SEARCH_PARAM_KEYS.includes(key)) continue;
    kept.push({
      key,
      value: value.slice(0, AGENT_PAGE_SEARCH_PARAM_VALUE_MAX_LENGTH),
    });
    if (kept.length >= AGENT_PAGE_SEARCH_PARAM_MAX_COUNT) break;
  }
  return kept.length > 0 ? kept : undefined;
}

/** Derives the entity ids (and allow-listed search params) from the current route. */
export function derivePageContext(pathname: string, search = ""): PageContext {
  const searchParams = deriveAgentPageSearchParams(search);
  const searchParamsField = searchParams ? { searchParams } : {};
  const quote = matchPath("/projects/:projectId/quotes/:quoteId/*", pathname);
  if (quote?.params.projectId && quote.params.quoteId) {
    return {
      path: pathname,
      projectId: quote.params.projectId,
      quoteId: quote.params.quoteId,
      ...searchParamsField,
    };
  }
  const requirement = matchPath(
    "/projects/:projectId/requirements/:requirementId/*",
    pathname
  );
  if (requirement?.params.projectId && requirement.params.requirementId) {
    return {
      path: pathname,
      projectId: requirement.params.projectId,
      requirementId: requirement.params.requirementId,
      ...searchParamsField,
    };
  }
  const project = matchPath("/projects/:projectId/*", pathname);
  if (project?.params.projectId) {
    return {
      path: pathname,
      projectId: project.params.projectId,
      ...searchParamsField,
    };
  }
  const template = matchPath(
    "/templates/documents/:templateId/:version/*",
    pathname
  );
  if (template?.params.templateId) {
    return {
      path: pathname,
      templateId: template.params.templateId,
      ...searchParamsField,
    };
  }
  const crmNestedPerson = matchPath(
    "/crm/organisations/:organisationId/users/:userId/*",
    pathname
  );
  if (crmNestedPerson?.params.organisationId && crmNestedPerson.params.userId) {
    return {
      path: pathname,
      crmCompanyId: crmNestedPerson.params.organisationId,
      crmPersonId: crmNestedPerson.params.userId,
      ...searchParamsField,
    };
  }
  const crmCompany = matchPath("/crm/organisations/:id/*", pathname);
  if (crmCompany?.params.id) {
    return {
      path: pathname,
      crmCompanyId: crmCompany.params.id,
      ...searchParamsField,
    };
  }
  const crmPerson = matchPath("/crm/users/:id/*", pathname);
  if (crmPerson?.params.id) {
    return {
      path: pathname,
      crmPersonId: crmPerson.params.id,
      ...searchParamsField,
    };
  }
  return { path: pathname, ...searchParamsField };
}
